import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { CredentialRequestListTemplate } from "@/features/credential-requests/templates";
import {
  useUserProfile,
  useCredentialRequestList,
  useRequestListActions,
} from "@/features/credential-requests/hooks";
import { createNacionalFunctionRequest } from "@/data/credentialReqRepository";
import { CREDENTIAL_TYPES } from "@/models/credentialRequest";
import { useAuth } from "@/context/authContext";
import { useInstitution } from "@/features/institutions/hooks/useInstitution";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import type { CredentialRequest } from "@/features/credential-requests/types";

const DN_SLUG = "directorio-nacional";

export default function NacionalCredentialsTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile, isAdmin } = useUserProfile();
  const { data: institution, isLoading: institutionLoading } = useInstitution(DN_SLUG);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "year", desc: true }]);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");

  const { requests, isLoading, totalPages, totalItems, reloadRequests } =
    useCredentialRequestList({
      statusFilter,
      districtFilter: "",
      regionFilter: "",
      dateFromFilter,
      dateToFilter,
      searchValue,
      sorting,
      currentPage,
      pageSize,
      credentialType: CREDENTIAL_TYPES.FUNCTION,
      institutionId: institution?.id,
    });

  const { isSubmitting, handleApprove, handleReject, handleMarkPrinted, handleMarkDelivered } =
    useRequestListActions({
      userId: userProfile?.id,
      onSuccess: reloadRequests,
    });

  const handleRowClick = (request: CredentialRequest) => {
    navigate(`/credential-requests/${request.id}`);
  };

  const handleCreateNew = async () => {
    if (!user || !institution) return;
    const year = new Date().getFullYear();
    const req = await createNacionalFunctionRequest(year, user.id, institution.id);
    navigate(`/credential-requests/${req.id}`);
  };

  if (institutionLoading) {
    return (
      <div className="flex justify-center py-12">
        <InlineLoader message="Cargando..." />
      </div>
    );
  }

  return (
    <CredentialRequestListTemplate
      hideHeader
      requests={requests}
      isLoading={isLoading}
      isAdmin={isAdmin}
      isSubmitting={isSubmitting}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      searchValue={searchValue}
      onSearchChange={(v) => { setSearchValue(v); setCurrentPage(1); }}
      sorting={sorting}
      onSortingChange={setSorting}
      statusFilter={statusFilter}
      onStatusFilterChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
      districtFilter=""
      onDistrictFilterChange={() => {}}
      regionFilter=""
      onRegionFilterChange={() => {}}
      dateFromFilter={dateFromFilter}
      onDateFromFilterChange={(v) => { setDateFromFilter(v); setCurrentPage(1); }}
      dateToFilter={dateToFilter}
      onDateToFilterChange={(v) => { setDateToFilter(v); setCurrentPage(1); }}
      districts={[]}
      regions={[]}
      onRowClick={handleRowClick}
      onApprove={(req) => handleApprove(req.id)}
      onReject={(reqId, notes) => handleReject(reqId, notes)}
      onMarkPrinted={(req) => handleMarkPrinted(req.id)}
      onMarkDelivered={(req) => handleMarkDelivered(req.id)}
      onCreateNew={handleCreateNew}
      columnOptions={{ hideDistrict: true, hideRegion: true }}
      filterOptions={{ hideDistrictRegion: true }}
    />
  );
}
